import { reactive } from 'vue';
import makeStoreDebugAccessible from '@/core/store/store-debug';
import utilities from '@/shared/utilities.service';
import { IBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import { B2CClientService, UpdateWishListRequest, WishList } from '@/b2c/shared/api/server-apis';
import { QueryKeys } from '@/shared/config/route-query.config';

class WishListsStore {
    private state: IWishListsState = reactive({
        details: {} as WishList,
        shared: {} as WishList,
        lists: [] as (WishList | undefined)[],
        pending: false,
    });

    constructor() {
        makeStoreDebugAccessible('wishListsStore', this.state);
    }

    async updateLists() {
        this.state.pending = true;
        const lists = await B2CClientService.getAllWishLists().then(utilities.unwrapModel);
        this.state.lists = lists || [];
        this.state.pending = false;
        return this.state.lists;
    }

    async createList(name: string) {
        const updatedLists = await B2CClientService.createWishList({ name }).then(utilities.unwrapModel);
        return this.state.lists = updatedLists || [];
    }

    async deleteList(id: string) {
        const updatedLists = await B2CClientService.deleteWishList(Number(id)).then(utilities.unwrapModel);
        return this.state.lists = updatedLists || [];
    }

    async updateList(id: number, name: string) {
        const request:UpdateWishListRequest = {
            name,
        };
        this.state.details.name = name;
        return await B2CClientService.updateWishList(id, request);
    }

    async updateDetails(id: number): Promise<WishList> {
        const details = await B2CClientService.getWishList(id).then(utilities.unwrapModel);

        return this.state.details = details ?? {} as WishList;
    }

    async updateShared(identifier: string): Promise<WishList> {
        const shared = await B2CClientService.getSharedWishList(identifier).then(utilities.unwrapModel);

        return this.state.shared = shared ?? {} as WishList;
    }

    async deleteItem(id: string, itemId: number) {
        const details = await B2CClientService.deleteWishListItem(Number(id), itemId).then(utilities.unwrapModel);

        return this.state.details = details ?? {} as WishList;
    }

    async clearDetails() {
        this.state.details = {} as WishList;
    }

    async clearShared() {
        this.state.shared = {} as WishList;
    }

    get lists(): (WishList | undefined)[] {
        return this.state.lists;
    }

    get details(): WishList {
        return this.state.details;
    }

    get shared(): WishList {
        return this.state.shared;
    }

    get pending(): boolean {
        return this.state.pending;
    }

    getDetailsShareImageUrl(details: WishList): string | undefined {
        const items = details?.wishListItems;
        const defaultProductSku = items && items.length ? items[0].variant.sku : undefined;
        return defaultProductSku ? utilities.getProductImageUrlFromSku(defaultProductSku, 640) : undefined;
    }

    getEmptyResultText(labels: IBlockLabels): string {
        return this.state.pending ? labels.loading : labels.noItemsAdded;
    }

    getDetailsUrl(id: number, detailsUrl: string) {
        return {
            path: detailsUrl,
            query: {
                [QueryKeys.Id]: id.toString(),
            },
        };
    }

    getFormattedShareUrl(shareUrl: string): string {
        return `${location.origin}${shareUrl}?${QueryKeys.Id}=${this.state.details.shareIdentifier}`;
    }
}

export interface IWishListsState {
    lists: (WishList | undefined)[];
    details: WishList;
    shared: WishList;
    pending: boolean;
}

export default new WishListsStore();
